import { faTrashCan } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Checkbox, FormControlLabel, Grid, Paper, TextField, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReportCustomTemplateField, ReportTemplate } from "../../api/dtos";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton, StyledIconButton } from "../../components/StyledButton";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import usePostReportTemplateSave from "../../hooks/data/usePostReportTemplateSave";
import useReportCustomTemplateFields from "../../hooks/data/useReportCustomTemplateFields";
import useReportTemplateById from "../../hooks/data/useReportTemplateById";
import useReportTemplateFieldsByTemplateId from "../../hooks/data/useReportTemplateFieldsByTemplateId";

interface IReportCustomTemplateProps {
    edit?: boolean
}

const ReportCustomTemplate: FC<IReportCustomTemplateProps> = ({
    edit
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const theme = useTheme();
    const save = usePostReportTemplateSave();
    const snackbar = useSnackbar();
    const templateId = edit ? Number(params.templateId) : undefined;

    const {data: template} = useReportTemplateById(templateId);
    const {data: templateFields} = useReportTemplateFieldsByTemplateId(templateId);

    const [formFieldsData, setFormFieldsData] = useState<ReportCustomTemplateField[]>([]);
    const [templateName, setTemplateName] = useState<string | undefined>();
    const {data: customFields} = useReportCustomTemplateFields();

    useEffect(() => {
        setTemplateName(template?.reportTemplate?.name);
        setFormFieldsData(templateFields ?? [])
    }, [template, templateFields, customFields])

    const handleTextFieldChange = (event: any) => {
        setTemplateName(event.target.value);
    }

    const handleCheckboxChange = (event: any, field: ReportCustomTemplateField) => {
        if(event.target.checked) {
            const index = formFieldsData.findIndex(x => x.fieldType == field.fieldType && x.fieldReferenceId == field.fieldReferenceId);
            if(index == -1 && field) {
                setFormFieldsData([...formFieldsData, field]);
            }
        } else {
            handleRemoveField(field);
        }
    }

    const handleRemoveField = (field: ReportCustomTemplateField) => {
        const index = formFieldsData.findIndex(x => x.fieldType == field.fieldType && x.fieldReferenceId == field.fieldReferenceId);
        if(index != -1 && field) {
            const newFields = formFieldsData;
            newFields.splice(index, 1);
            setFormFieldsData(_old => [...newFields])
        }
    }

    const handleTemplateSave = async () => {
        save(new ReportTemplate({id: templateId, name: templateName}), formFieldsData)
            .then(() => {
                snackbar.enqueueSnackbar({ variant: "success", message: "Report Template Saved Successfully" });
                if(!edit) {
                    navigate("/reports/templates")
                }
            })
            .catch(() => {
                snackbar.enqueueSnackbar({ variant: "error", message: "Report Template Could Not Be Saved" });
            });
    }

    return (
        <>
            <SetLinks
                lotLinkEnabled={false}
                stageLinkEnabled={false}
                createProjectLink={DefaultProjectCreateLink}
            />
            <Grid container>

                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 10,
                        xl: 8
                    }}
                >
                    <Paper
                        sx={{
                            padding: 2
                        }}
                    >
                        <TextField
                            fullWidth
                            label="Name"
                            value={templateName ?? ""}
                            onChange={handleTextFieldChange}
                        />
                        <Grid
                            container
                            spacing={theme.spacing(2)}
                        >
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 6,
                                    lg: 6,
                                    xl: 6
                                }}
                            >
                                <Box
                                    sx={{
                                        maxHeight: 500,
                                        overflow: "auto",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {
                                        customFields?.map((field, index) => (
                                            <FormControlLabel 
                                                control={
                                                    <Checkbox 
                                                        key={index} 
                                                        value={index} 
                                                        checked={formFieldsData.filter(x => field.field.fieldType == x.fieldType && x.fieldReferenceId == field.field.fieldReferenceId).length > 0}
                                                        onChange={event => handleCheckboxChange(event, field.field)}
                                                    />} 
                                                label={field.name}
                                            />
                                        ))
                                    }
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    sm: 6
                                }}
                            >
                                <Box
                                    sx={{
                                        maxHeight: 500,
                                        overflow: "auto",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {
                                        formFieldsData.map(field => (
                                            <Grid
                                                sx={{
                                                    margin: theme.spacing(0.5, 0)
                                                }}
                                                container
                                            >
                                                <Grid
                                                    size={{
                                                        xs: 11
                                                    }}
                                                >
                                                    {customFields?.filter(x => x.field.fieldType == field.fieldType && x.field.fieldReferenceId == field.fieldReferenceId)[0]?.name}
                                                </Grid>
                                                <Grid
                                                    size={{
                                                        xs: 1
                                                    }}
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end"
                                                    }}
                                                >
                                                    <StyledIconButton
                                                        size={"small"}
                                                        onClick={() => handleRemoveField(field)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrashCan} />
                                                    </StyledIconButton>
                                                </Grid>
                                            </Grid>
                                        ))
                                    }
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12
                            }}
                            sx={{
                                mt: 2,
                                display: "flex",
                                justifyContent: "right"
                            }}
                        >
                            <StyledButton
                                variant="contained"
                                onClick={handleTemplateSave}
                            >
                                Save Template
                            </StyledButton>
                        </Grid>
                        {/* </Select> */}
                    </Paper>
                </Grid>
            </Grid>
        </> 
    );
}

export default ReportCustomTemplate;